import React, { useState, useEffect, useRef } from "react";
import ProfileCommentList from "./ProfileCommentList";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import loadingAnimationTablet from "../assets/animations/profileLoadingLg.json";
import loadingAnimationMobile from "../assets/animations/profileLoadingSm.json";
import { useLottie } from "lottie-react";
import LoadingSpinner from "./LoadingSpinner";

function Profile(props) {
  const statusSelect = useRef();
  const [isLoggedInUsersOwnProfile, setIsLoggedInUsersOwnProfile] = useState(false);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isMdViewport, setIsMdViewport] = useState(false);
  const [hasCommentType, setHasCommentType] = useState({});
  const [pageLoaded, setPageLoaded] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [title, setTitle] = useState("");
  const [statusId, setStatusId] = useState(0);
  const { memberId, isCurrentOrganisation, numberOfComments } = props;

  const handleResize = debounce(() => {
    setIsMdViewport(window.innerWidth >= 750);
  }, 100);

  const handleSelectChange = (e) => {
    e.target?.value !== "" && setSelectedStatus(e.target.value);

    switch (e.target?.value.toString()) {
      case "pending":
        setTitle("pending comments");
        setStatusId(1);
        break;
      case "approved":
        setTitle("comments");
        setStatusId(2);
        break;
      case "awaiting":
        setTitle("comments awaiting edit");
        setStatusId(5);
        break;
    }
  };

  const updateHasCommentType = (id) => {
    const newHasCommentTypeObj = hasCommentType;

    let commentType;
    switch (id.toString()) {
      case "1":
        commentType = "pending";
        break;
      case "2":
        commentType = "approved";
        break;
      case "5":
        commentType = "awaiting";
        break;
      default:
        commentType = "undefined";
    }

    newHasCommentTypeObj[commentType] = true;
    setHasCommentType(newHasCommentTypeObj);
  };

  const Animation = () => {
    const options = {
      animationData: isMdViewport
        ? loadingAnimationTablet
        : loadingAnimationMobile,
      loop: true,
      autoplay: true,
    };
    const { View } = useLottie(options, {});
    return View;
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  });

  useEffect(() => {
    (async () => {
      // Populate and show number of comments
      if (numberOfComments) {
        const numberOfCommentsWrapperElement = document.querySelector(
          "#number-of-comments-wrapper"
        );
        const numberOfCommentsElement = numberOfCommentsWrapperElement?.querySelector(
          "#number-of-comments"
        );

        if (!loading && numberOfCommentsElement) {
          numberOfCommentsElement.innerText = `${numberOfComments.toString()} ${
            numberOfComments === "1" ? "Comment" : "Comments"
          }`;
          numberOfCommentsWrapperElement.classList.add("d-flex");
          numberOfCommentsWrapperElement.classList.remove("d-none");
        }
      }

      setPageLoaded(true);
    })();
  }, [loading, memberId]);

  useEffect(() => {
    const loggedInUserId = document.querySelector("#root")?.dataset.userId;
    setIsLoggedInUsersOwnProfile(loggedInUserId && ((loggedInUserId === memberId) || isCurrentOrganisation === "True"));

    if (isLoggedInUsersOwnProfile && !isEmpty(hasCommentType)) {
      setSelectedStatus(hasCommentType.pending ? "pending" : "approved");
      setTitle(hasCommentType.pending ? "pending comments" : "comments");
      setStatusId(hasCommentType.pending ? 1 : 2);
    } else {
      setSelectedStatus("approved");
      setTitle("comments");
      setStatusId(2);
    }
  }, [memberId, isLoggedInUsersOwnProfile, hasCommentType]);

  if (loading && commentsLoaded) {
    setLoading(false);
  }

  return (
    <>
      {pageLoaded ? (
          <>
          <h2 className="ugc-profile__comments-title">Comments and Photos</h2>

            {
              isLoggedInUsersOwnProfile && 
            <div className="ugc-comments-list__dropdown ugc-profile__comments-status">
                <label className="ugc-profile__comments-status-label" htmlFor="status">
                  Status
                </label>
                <div className="dropdown-container ugc-profile__comments-status-dropdown">
                  <select
                    ref={statusSelect}
                    className="dropdown-field ugc-profile__comments-status-field"
                    name="status"
                    id="status"
                    value={selectedStatus}
                    onChange={handleSelectChange}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="awaiting">Awaiting edit</option>
                  </select>
                </div>
              </div>
            }

            <div aria-live="polite" aria-busy={!commentsLoaded}>
              {
                !commentsLoaded && (
                <div className="ugc-profile__loading">
                  <p className="ugc-profile__loading-text">
                    Loading comments...
                  </p>
                  <Animation/>
                </div>
                )}

              <ProfileCommentList
                top={10}
                skip={0}
                id={memberId}
                isLoggedInUsersOwnProfile={isLoggedInUsersOwnProfile}
                updateHasCommentType={updateHasCommentType}
                title={title}
                statusId={statusId}
                loadingComplete={setCommentsLoaded}
                selectedStatus={selectedStatus}
              />

              {!loading && props.numberOfComments === 0 && statusId === 2 && (
                <>
                {isLoggedInUsersOwnProfile ?
                  <p>You have not made any contributions yet. Find out about the
                      <a href="https://historicengland.org.uk/listing/missing-pieces/"> Missing Pieces Project </a>
                  </p> :
                  <p>This person has yet to comment</p>}
                </>
              )}
            </div>
          </>
      ) : (
        <LoadingSpinner />
      )}
    </>
  );
}

export default Profile;
